import { Component, Input, OnInit } from "@angular/core";
import { TranslocoService } from "@ngneat/transloco";
import { NzModalRef } from "ng-zorro-antd/modal";
import { NzMessageService } from "ng-zorro-antd/message";
import { LoadingService } from "../../../core/services/loading.service";
import { ItineraryService } from "../../../core/services/itinerary.service";
import { ItineraryTemplatesService } from "../../../core/services/itinerary-templates.service";
import * as moment from "moment";
import { getAccommodationPerDate } from "../../../core/ult-functions/accommodation";
import {displayTitle} from "../../../core/ult-functions/common";
import { withItineraryLockSession } from "../../../core/ult-functions/itinerary-tab-session";

@Component({
  selector: "app-move-delete-days",
  templateUrl: "./move-delete-days.component.html",
  styleUrls: ["./move-delete-days.component.scss"],
})
export class MoveDeleteDaysComponent implements OnInit {
  @Input() days: any;
  @Input() accommodation: any;
  @Input() experiences: any;
  @Input() id: any;
  @Input() tourInfo: any;
  @Input() itineraryMode: 'itinerary' | 'template' = 'itinerary';
  @Input() sessionId?: string;

  draggedIndex: number | null = null;
  isButtonLoading = false;

  get activeService(): ItineraryService | ItineraryTemplatesService {
    return this.itineraryMode === 'template' ? this.itineraryTemplatesService : this.itineraryService;
  }

  constructor(
    private modal: NzModalRef,
    private nzMessageService: NzMessageService,
    private loading: LoadingService,
    private itineraryService: ItineraryService,
    private itineraryTemplatesService: ItineraryTemplatesService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {}

  handleCancel(): void {
    this.modal.close({ message: "error" });
  }

  getDayLabel(dayIndex: number, date: any, destinationTitles: any[]): string {
    const dayLabel = this.translocoService.translate("itineraries.moveDeleteDays.dayLabel", {
      day: dayIndex + 1
    });
    const dateLabel = !this.tourInfo?.isDatesOptional ? ` - ${moment(date).format("MMM D, YYYY")}` : "";
    const destinationLabel = destinationTitles?.length
      ? ` - ${displayTitle(destinationTitles, this.tourInfo.language)}`
      : "";

    return `${dayLabel}${dateLabel}${destinationLabel}`;
  }

  getDeleteConfirmTitle(): string {
    return this.translocoService.translate("itineraries.moveDeleteDays.deleteConfirmTitle");
  }

  getAccommodationRemovedMessage(name: string): string {
    return this.translocoService.translate("itineraries.moveDeleteDays.messages.accommodationRemoved", {
      name
    });
  }

  async save(): Promise<void> {
    this.loading.start();
    this.isButtonLoading = true;
    try {
      await this.activeService.updateItineraryLockState(withItineraryLockSession({
        _id: this.id,
        experiences: this.experiences,
        days: this.days,
        tour: this.tourInfo,
        accommodation: this.accommodation,
      }, this.sessionId!));
      this.nzMessageService.success(this.translocoService.translate("itineraries.moveDeleteDays.messages.changesSaved"));
      this.modal.close({ message: "success" });
    } catch (error: any) {
      const msg =
        error?.error?.message || error?.message?.message || error?.message || this.translocoService.translate("itineraries.moveDeleteDays.messages.somethingWentWrong");
      this.nzMessageService.error(msg);
      this.modal.close({ message: "error" });
    } finally {
      this.isButtonLoading = false;
      this.loading.stop();
    }
  }

  onConfirmDeleteExperiences(dayIndex: number): void {
    this.loading.start();
    this.isButtonLoading = true;
    if (dayIndex === 0) {
      this.tourInfo.startDate = moment(this.tourInfo.startDate)
        .add(1, "days")
        .toISOString();
    } else {
      this.tourInfo.endDate = moment(this.tourInfo.endDate)
        .subtract(1, "days")
        .toISOString();
    }

    const getAccPerDate = getAccommodationPerDate(this.days[dayIndex].date, this.accommodation);
    const getArrayId = this.filterByDate(getAccPerDate, this.days[dayIndex].date);

    if (getArrayId !== "") {
      const accIndex  = this.accommodation.findIndex((acc: any) => acc._id === getArrayId);
      this.nzMessageService.success(this.getAccommodationRemovedMessage(this.accommodation[accIndex].name));
      this.accommodation.splice(accIndex, 1);
    }

    this.days.splice(dayIndex, 1);
    this.experiences.splice(dayIndex, 1);

    this.tourInfo.noOfDays = this.days.length;

    for (let i = 0; i < this.tourInfo.noOfDays; i++) {
      const date = moment(this.tourInfo.startDate).add(i, "days");
      // update the day's date itself
      this.days[i].date = date.toDate();

      // update all transport selectedDate for that day
      this.days[i].transport.forEach((t: any) => {
        t.selectedDate = date.toDate();
      });
      // update all experiences selectedDate for that day
      this.experiences[i].date = date.toDate();
    }

    this.loading.stop();
    this.isButtonLoading = false;
  }

  cancel(): void {}

  filterByDate(arr: any[], givenDate: string): string {
    if (!arr.length) {
      return "";
    }

    const item = arr[0];
    const checkIn = moment(item.checkIn);
    let checkOut = moment(item.checkOut);
    const date = moment(givenDate);

    if (!checkOut.isSame(checkIn, "day")) {
      checkOut = checkOut.subtract(1, "day");
    }
    if (date.isSame(checkIn, "day")) {
      return item._id;
    }
    if (date.isBetween(checkIn, checkOut, "day", "[]")) {
      return item._id;
    }
    return "";
  }

  onDaysDragStart(event: any, index: number): void {
    this.draggedIndex = index;
  }

  onDaysDragOver(event: any): void {
    event.preventDefault();
  }

  onDaysDrop(event: any, targetIndex: number): void {
    event.preventDefault();

    const draggedIndex = this.draggedIndex;
    if (draggedIndex === null) {
      return;
    }

    // Call a method to reorder the form array
    this.reorderDaysArray(draggedIndex, targetIndex);
    this.draggedIndex = null;
  }

  reorderDaysArray(fromIndex: number, toIndex: number): void {
    if (fromIndex === toIndex) {
      return;
    }

    const fromGetAccPerDate = getAccommodationPerDate(this.days[fromIndex].date, this.accommodation);
    const fromGetArrayId = this.filterByDate(fromGetAccPerDate, this.days[fromIndex].date);

    if (fromGetArrayId !== "") {
      const accIndex  = this.accommodation.findIndex((acc: any) => acc._id === fromGetArrayId);
      this.nzMessageService.success(this.getAccommodationRemovedMessage(this.accommodation[accIndex].name));
      this.accommodation.splice(accIndex, 1);
    }

    const toGetAccPerDate = getAccommodationPerDate(this.days[fromIndex].date, this.accommodation);
    const toGetArrayId = this.filterByDate(toGetAccPerDate, this.days[fromIndex].date);

    if (toGetArrayId !== "") {
      const accIndex  = this.accommodation.findIndex((acc: any) => acc._id === toGetArrayId);
      this.nzMessageService.success(this.getAccommodationRemovedMessage(this.accommodation[accIndex].name));
      this.accommodation.splice(accIndex, 1);
    }

    const datesSnapshot = this.days.map((d: any) => d.date);

    const movedDay = this.days.splice(fromIndex, 1)[0];
    this.days.splice(toIndex, 0, movedDay);

    const movedExperience = this.experiences.splice(fromIndex, 1)[0];
    this.experiences.splice(toIndex, 0, movedExperience);

    // Reapply the original dates to their indices and sync transport/experiences
    for (let i = 0; i < this.days.length; i++) {
      const dateForIndex = datesSnapshot[i];
      this.days[i].date = dateForIndex;
      if (Array.isArray(this.days[i].transport)) {
        this.days[i].transport.forEach((t: any) => {
          t.selectedDate = dateForIndex;
        });
      }
      if (this.experiences[i]) {
        this.experiences[i].date = dateForIndex;
      }
    }
  }

  protected readonly displayTitle = displayTitle;
}
